import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setAuthView,
  setResetEmail,
  setUsernames,
  setPrefilledUsername,
  setForgotUsername,
  setForgotTempPassword,
  setVerificationCode,
} from '../../redux/slice/authModalSlice';
import { Images } from '../../utils/imageConstants';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { forgotPasswordSchema } from './validation/forgotPasswordSchema';
import { useMutation } from '@tanstack/react-query';
import { Api } from '../../services/interceptor';
import { QUERY_KEY } from '../../services/queryEndPoint';
import {
  ForgotPasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import toast from 'react-hot-toast';
import type { ForgotPasswordFormValues } from './types/auth.d';
import { clientRegion, generateSecretHash } from '../../helper/helper';

const ForgotPassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isForgotUsername } = useAppSelector((s) => s.authModal);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, setError } = useForm<ForgotPasswordFormValues>({
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  // Check if email exists and get usernames
  const checkEmailMutation = useMutation<string[]>({
    mutationFn: async (email: string) => {
      // #region agent log
      fetch("http://127.0.0.1:7670/ingest/31dbf84c-329f-469a-9e85-2355049cac92", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "271729" },
        body: JSON.stringify({
          sessionId: "271729",
          location: "ForgotPassword.tsx:checkEmailMutation:before",
          message: "checkEmail input shape",
          data: {
            emailLen: email?.length ?? 0,
            hasAt: typeof email === "string" && email.includes("@"),
            hasPlus: typeof email === "string" && email.includes("+"),
            needsEncode:
              typeof email === "string" && encodeURIComponent(email) !== email,
          },
          timestamp: Date.now(),
          hypothesisId: "H2-H3",
        }),
      }).catch(() => {});
      // #endregion
      const response = await Api.get(`${QUERY_KEY.getUserNamesByEmail}/${email}`);
      const raw = response?.data;
      const parsed = Array.isArray(raw) ? raw : raw?.userNames || [];
      // #region agent log
      fetch("http://127.0.0.1:7670/ingest/31dbf84c-329f-469a-9e85-2355049cac92", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "271729" },
        body: JSON.stringify({
          sessionId: "271729",
          location: "ForgotPassword.tsx:checkEmailMutation:after",
          message: "checkEmail response parsed",
          data: {
            httpStatus: response?.status,
            rawType: raw === null ? "null" : typeof raw,
            rawIsArray: Array.isArray(raw),
            rawKeys: raw && typeof raw === "object" && !Array.isArray(raw) ? Object.keys(raw as object) : [],
            userNamesType: raw && typeof raw === "object" && !Array.isArray(raw) ? typeof (raw as { userNames?: unknown }).userNames : "n/a",
            parsedLength: Array.isArray(parsed) ? parsed.length : -1,
            parseBranch: Array.isArray(raw) ? "array" : "userNamesOrEmpty",
          },
          timestamp: Date.now(),
          hypothesisId: "H1-H5",
        }),
      }).catch(() => {});
      // #endregion
      // API returns array of usernames directly
      return parsed;
    },
  });

  // Send OTP via Cognito
  const sendOtpMutation = useMutation({
    mutationFn: async (email: string) => {
      const secretHash = generateSecretHash(email);
      
      const command = new ForgotPasswordCommand({
        ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
        Username: email,
        SecretHash: secretHash,
      });

      await clientRegion.send(command);
    },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    setIsSubmitting(true);
    
    try {
      if (isForgotUsername) {
        // Forgot Username flow: Get usernames by email
        const usernames = await checkEmailMutation.mutateAsync(values?.email);
        if (!usernames || usernames.length === 0) {
          setError('email', { type: 'manual', message: 'No account found with this email' });
          toast.error('No account found with this email');
          setIsSubmitting(false);
          return;
        }

        // If only one username, skip dropdown and go directly to sign-in
        if (usernames.length === 1) {
          dispatch(setPrefilledUsername(usernames[0]));
          dispatch(setForgotUsername(false));
          dispatch(setUsernames(undefined));
          dispatch(setAuthView('signin'));
          toast.success('Username found');
        } else {
          // Multiple usernames: Store usernames and navigate to MultipleUsers view
          dispatch(setUsernames(usernames));
          dispatch(setResetEmail(values.email));
          dispatch(setAuthView('multipleUsers'));
          // toast.success('Usernames retrieved successfully');
        }
      } else {
        // Forgot Password flow: Check email and send OTP
        const emailCheckResult = await checkEmailMutation.mutateAsync(values.email);
        
        // Check if user exists 
        if (!emailCheckResult || emailCheckResult.length === 0) {
          setError('email', { type: 'manual', message: 'Email does not exist' });
          toast.error('Email does not exist');
          setIsSubmitting(false);
          return;
        }

        // Step 2: Send OTP via Cognito
        await sendOtpMutation.mutateAsync(values.email);

        // Step 3: Success - Open OTP modal (clear any stale reset state)
        dispatch(setForgotTempPassword(undefined));
        dispatch(setVerificationCode(undefined));
        dispatch(setResetEmail(values.email));
        dispatch(setAuthView('otp'));
        toast.success('Verification code sent to your email');
      }
      
    } catch (err: any) {
      console.error('Forgot password/username error:', err);
      // #region agent log
      fetch("http://127.0.0.1:7670/ingest/31dbf84c-329f-469a-9e85-2355049cac92", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Debug-Session-Id": "271729" },
        body: JSON.stringify({
          sessionId: "271729",
          location: "ForgotPassword.tsx:onSubmit:catch",
          message: "forgot flow error",
          data: {
            status: err?.response?.status,
            code: err?.code,
            errName: err?.name,
            hasUserNotFound: typeof err?.message === "string" && err.message.includes("UserNotFoundException"),
          },
          timestamp: Date.now(),
          hypothesisId: "H4",
        }),
      }).catch(() => {});
      // #endregion
      if (err?.response?.status === 404) {
        setError('email', { type: 'manual', message: 'Email does not exist' });
        toast.error('Email does not exist');
      } else if (err?.message?.includes('UserNotFoundException')) {
        setError('email', { type: 'manual', message: 'Email does not exist' });
        toast.error('Email does not exist');
      } else {
        const errorMessage = err?.message || (isForgotUsername ? 'Failed to retrieve usernames' : 'Failed to send verification code');
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="figma-login-container">
      <div className="login-background-image">
        <div className="login-modal-container">
          <div className="login-header">
            <div className="logo-container">
              <img src={Images.main_logo} alt="CRICCENTER" className="logo" />
            </div>
            <h1 className="app-name">CRICCENTER</h1>
          </div>
          
          <h2 className="signin-title">{isForgotUsername ? 'Forgot Username?' : 'Forgot Password?'}</h2>
          
          <form onSubmit={handleSubmit(onSubmit)} className="login-form">
            <div className="form-group">
              <label htmlFor="forgotEmail" className="form-label">
                {isForgotUsername ? 'Email' : 'Email / User Id'}
              </label>
              <div className="input-wrapper">
                <input
                  id="forgotEmail"
                  type="email"
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  placeholder={isForgotUsername ? 'Enter Email Address' : 'Enter Email Address or User Id'}
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <span className="error-message">{errors.email.message as string}</span>
              )}
            </div>
            
            <div className="action-buttons">
              <button 
                type="submit" 
                className="btn-primary" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (isForgotUsername ? 'Loading...' : 'Sending...') : (isForgotUsername ? 'Next' : 'Send OTP')}
              </button>
              
              <button 
                type="button" 
                className="btn-secondary"
                onClick={() => {
                  dispatch(setForgotUsername(false));
                  dispatch(setAuthView('signin'));
                }}
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
